import React, { useEffect, useState } from "react";
import { HiOutlineTrash, HiPlusSm } from "react-icons/hi";
import WorkerLeaveModal from "./WorkerLeaveModal";
import { apiDeleteRequest, apiGetRequest } from "helpers/Requests";
import { getNameProfile } from "Utils/utils";
import LeaveDeleteModal from "./LeaveDeleteModal";
import PaginationComponent from "components/common/Pagination";
import {
  emptyPaginationObj,
  POLICIES_LIST_LIMIT,
  USER_ROLE,
} from "Constants/constant";
import { getPaginationObj } from "Utils/utils";
import LeaveUpdateModal from "./LeaveUpdateModal";
import { roleAccess } from "helpers/RolesPermission";
import { parseISO, format } from "date-fns";
import CircularLoader from "components/common/CircularLoader/CircularLoader";

const WorkerLeave = ({
  isUserGracePeriodOver,
  WorkerUuid,
  WorkerId,
  worker,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leaves, setLeaves] = useState([]);
  const [isShowModal, setIsShowModal] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [paginationObj, setPaginationObj] = useState(emptyPaginationObj);
  const [page, setPage] = useState(1);

  const formatDisplayDate = (dateValue) => {
    if (!dateValue) return "-";
    try {
      return format(parseISO(dateValue), "dd-MMM-yyyy");
    } catch {
      return format(new Date(dateValue), "dd-MMM-yyyy");
    }
  };

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const response = await apiGetRequest(
        `/sp/worker-leave?worker_uuid=${WorkerUuid}&page=${page}&per_page=${POLICIES_LIST_LIMIT}`
      );
      const pagePayload = response.data?.data;
      const fetchedLeaves = pagePayload?.data || [];
      setLeaves(fetchedLeaves);
      setPaginationObj(getPaginationObj(pagePayload));
    } catch (error) {
      console.error("Error fetching worker leaves:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (WorkerUuid) {
      fetchLeaves();
    }
  }, [WorkerUuid, page]);

  const renderStatusBadge = (status) => {
    const normalized = (status || "").toLowerCase();
    const baseClass =
      "inline-flex items-center px-2 py-1 rounded text-sm font-semibold capitalize";

    if (normalized === "pending") {
      return (
        <span className={`${baseClass} bg-yellow-100 text-yellow-700`}>
          Pending
        </span>
      );
    }
    if (normalized === "expired") {
      return (
        <span className={`${baseClass} bg-red-100 text-red-700`}>
          Expired
        </span>
      );
    }
    if (normalized === "approved") {
      return (
        <span className={`${baseClass} bg-green-100 text-green-700`}>
          Approved
        </span>
      );
    }
    if (normalized === "rejected") {
      return (
        <span className={`${baseClass} bg-red-100 text-red-700`}>
          Rejected
        </span>
      );
    }
    return (
      <span className={`${baseClass} bg-gray-100 text-gray-700`}>
        {status || "-"}
      </span>
    );
  };

  return (
    <div>
      <label className="text-md font-bold">Worker Leave History</label>
      <div className="mt-1 mb-10">
        <div className="flex justify-end text-blue-400 ">
          <div
            className="flex items-center cursor-pointer "
            onClick={() => setOpenModal(true)}
          >
            <span>
              <HiPlusSm />
            </span>
            <span className="text-sm font-semibold">Add Leave Request</span>{" "}
          </div>
        </div>
        <div className="overflow-auto custom-scroll mt-1">
          <table className="border border-collapse w-full">
            <thead>
              <tr className="bg-[#e1e5f0]">
                <th className="border p-2">Requested Date</th>
                <th className="border p-2">Leave Type</th>
                <th className="border p-2">Start Date</th>
                <th className="border p-2">End Date</th>
                <th className="border p-2">Status</th>
                <th className="border p-2">Approved By</th>
                <th className="border p-2">Approval Date</th>
                <th className="border p-2">Reason</th>
                <th className="border p-2">Comments</th>
                <th className="border p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="text-center p-4">
                    <CircularLoader />
                  </td>
                </tr>
              ) : leaves.length > 0 ? (
                leaves.map((leave, index) => (
                  <tr key={index}>
                    <td className="border p-2 text-sm">
                      {formatDisplayDate(leave.requested_date)}
                    </td>
                    <td className="border p-2 text-sm">
                      {leave?.leave_type ||
                        (leave?.is_partial_leave ? "Partial Leave" : "Full Day Leave")}
                    </td>
                    <td className="border p-2 text-sm">
                      {formatDisplayDate(leave.start_date)}{" "}
                      {leave?.is_partial_leave && leave?.start_time
                        ? `${leave.start_time}`
                        : ""}
                    </td>
                    <td className="border p-2 text-sm">
                      {formatDisplayDate(leave.end_date)}{" "}
                      {leave?.is_partial_leave && leave?.end_time
                        ? `${leave.end_time}`
                        : ""}
                    </td>
                    <td className="border p-2 text-center text-sm">
                      {renderStatusBadge(leave.status)}
                    </td>
                    <td className="border p-2 text-sm capitalize">
                      {leave.approved_by || "N/A"}
                    </td>
                    <td className="border p-2 text-sm">
                      {leave.status === "approved" && leave.approval_date
                        ? formatDisplayDate(leave.approval_date)
                        : "N/A"}
                    </td>
                    <td className="border p-2 text-sm max-w-xs">
                      <div
                        className="truncate-3-lines break-words"
                        title={leave.details}
                      >
                        {leave.details || "N/A"}
                      </div>
                    </td>
                    <td className="border p-2 text-sm max-w-xs">
                      <div
                        className="truncate-3-lines break-words"
                        title={leave.rejection_notes}
                      >
                        {leave.rejection_notes || "N/A"}
                      </div>
                    </td>
                    <td className="border p-2 text-center text-xs">
                      <div className="flex justify-center gap-x-2">
                        <button
                          onClick={() => {
                            setSelectedLeaveId(leave.uuid);
                            setIsShowModal(true);
                          }}
                          className="icon-delete-wrapper"
                          title="Delete"
                        >
                          <HiOutlineTrash className="icon-size" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center p-2 text-red-600 text-xs"
                  >
                    No leave requests found!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {(paginationObj.total > POLICIES_LIST_LIMIT ||
        paginationObj.totalPages > 1) && (
        <div className="px-4 pb-4 max-w-full mx-auto">
          <PaginationComponent
            page={page}
            activeListLength={leaves}
            limit={POLICIES_LIST_LIMIT}
            totalList={paginationObj.total}
            handlePagination={(nextPage) => {
              setPage(nextPage);
            }}
            totalPages={paginationObj.totalPages}
          />
        </div>
      )}
      {openModal && (
        <WorkerLeaveModal
          isUserGracePeriodOver={isUserGracePeriodOver}
          openModal={openModal}
          handleClose={setOpenModal}
          WorkerId={WorkerId}
          onLeaveAdded={fetchLeaves}
          apiType="sp"
        />
      )}
      {isShowModal && (
        <LeaveDeleteModal
          isUserGracePeriodOver={isUserGracePeriodOver}
          isShow={isShowModal}
          handleClose={() => setIsShowModal(false)}
          leaveId={selectedLeaveId}
          workerUuid={WorkerUuid}
          onDeleted={fetchLeaves}
        />
      )}
      {isOpenModal && (
        <LeaveUpdateModal
          isUserGracePeriodOver={isUserGracePeriodOver}
          isOpen={isOpenModal}
          handleClose={() => setIsOpenModal(false)}
          leaveId={selectedLeaveId}
          workerUuid={WorkerUuid}
          onLeaveUpdated={fetchLeaves}
          leaveData={leaves.find((l) => l.uuid === selectedLeaveId)}
        />
      )}
    </div>
  );
};
export default WorkerLeave;
